/**
 * Y/N price pill with optional fee-adjusted American odds. Honours the
 * dashboard-only Fees/Raw toggle (see memory feedback_fees_toggle_scope.md):
 * toggle on → odds via effectivePriceForOdds.
 *
 * A button when given an `onClick`, a plain span when not (#490) — so a
 * read-only surface can quote a price through this pill instead of growing a
 * parallel price renderer that could disagree with the trading tables.
 */

import { cn } from '@/lib/infra/utils';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useFeeDisplayStatus, useFeeFor } from '@/services/hydraFees';
import {
  FEE_DISPLAY_MESSAGE,
  FeeScheduleMarker,
  feeDisplayCanHide,
} from '@/components/molecules/FeeScheduleMarker';
import { roundToCentiCents } from '@/lib/pricing/subCent';
import { americanOdds } from '../tradeModalBook';
import { crossedBookTooltip, effectivePriceForOdds, type CrossedBookRead } from './helpers';
import type { MarketSide, PriceMode } from '../types';

export interface PricePillProps {
  /** Display label inside the pill — typically 'Y'/'N', 'O'/'U'. */
  label: string;
  /** Raw cents; null renders an em-dash. */
  cents: number | null;
  /** Which side of the binary market this pill represents. */
  side: MarketSide;
  /** The toolbar's currently-active side — drives the highlight. */
  activeMarketSide: MarketSide;
  /**
   * The toolbar's currently-active price mode. Drives the fee
   * perspective applied to the displayed American odds:
   *   - 'maker' → pill shows the best bid (LIMIT-rest perspective) →
   *     maker fee rate
   *   - 'taker' → pill shows the best ask (crossing perspective) →
   *     taker fee rate
   * Without this prop the previous behaviour was to always apply the
   * maker rate — which under-quoted the fee in Taker mode on series
   * with maker-fee-bearing schedules, and silently did nothing on
   * quadratic series (maker rate = 0). See odds audit 2026-05-25.
   */
  priceMode: PriceMode;
  disabled: boolean;
  /** Optional test hook for a specific table pill. */
  testId?: string;
  /**
   * Trade dispatch. OMIT IT to render the pill read-only (#490): with no
   * handler the pill is a plain `<span>` — not a button, not focusable, no
   * hover affordance, and its tooltip drops the "Click to trade" line, which
   * would otherwise promise an interaction that doesn't exist.
   *
   * This is what lets a read-only surface quote a price through the SAME pill
   * the trading tables use, rather than hand-rolling a third price renderer
   * that could drift from them (cf. CompactOutcomeBands' "can never quote
   * different numbers"). Every trading call site passes it and is unchanged.
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Used to look up the per-series fee schedule when the dashboard's Fees
   * toggle is on. Polymarket / unknown tickers fall back to raw.
   */
  ticker?: string | null;
  /**
   * Maker-fee rebate fraction (0..1) to fold into maker-perspective odds.
   * Computed by the parent (org enrolled + MM Rebate toggle + live/pregame).
   * 0 = no rebate. Only applied when the pill's fee perspective is maker.
   */
  makerRebatePct?: number;
  /**
   * Kalshi-lag value signal (lib/divergence/kalshiLagValue): Poly + Unabated
   * moved and this venue's executable ask hasn't repriced yet — ≥2¢ below
   * both references, fresh feeds only. Fast-lived by nature; slated to grow
   * into an opt-in notification and eventually a bot trigger (the lib module
   * owns the one shared definition). While true the price font pulses gold.
   * Display-only — the parent computes it from the taker read regardless of
   * the pill's own price mode.
   */
  valueSignal?: boolean;
  /**
   * #167 — crossed-book tripwire. Non-null when the book behind this pill is
   * showing a best bid ABOVE its best ask beyond the feed's one-tick
   * allowance, i.e. while HydraEdge adjudicates or rebaselines it.
   *
   * Presentation ONLY. It adds a marker and a tooltip line; it does not
   * change `cents`, the odds, the click handler, or the disabled state — a
   * suspect book is still tradable, and the price dispatched on click is the
   * unmodified one displayed. The read is derived per render (see
   * `crossedBookRead`), so it disappears on its own when the book heals or
   * ownership falls back to a sane source.
   */
  crossedBook?: CrossedBookRead | null;
  /**
   * #651 — persistent side tint. Off by default so every existing call site
   * stays pixel-identical (same fail-closed shape as EventRows' showOpColumn
   * and lt2CardScope: the parent surface decides, never this atom).
   *
   * Without it the green/red side colour appears ONLY on the pill matching
   * the toolbar's active side, so the pair reads as "coloured vs grey" and
   * the hue tracks the TOOLBAR, not which side is Over/Yes. With it, hue
   * always carries SIDE and intensity carries ACTIVE — two separate channels,
   * so the active-side signal survives intact rather than being repainted.
   *
   * Deliberately never the only cue: the pill's own O/U (Y/N) label carries
   * the same fact in text, per the house rule that two states must be
   * distinguishable by shape as well as colour, not hue alone
   * (docs/260723LadderVizSpec-v2.1-clean.md FR2).
   */
  sideTint?: boolean;
}

export function PricePill({
  label,
  cents,
  side,
  activeMarketSide,
  priceMode,
  disabled,
  testId,
  onClick,
  ticker,
  makerRebatePct = 0,
  valueSignal = false,
  crossedBook = null,
  sideTint = false,
}: PricePillProps) {
  const isActive = side === activeMarketSide;
  // No handler → read-only. The pill keeps its layout, typography and side
  // colour so a read-only surface quotes the same number the same way, but
  // sheds every affordance that implies a click.
  const readOnly = onClick == null;
  // Hover tints are the affordance — a read-only span that lights up
  // under the cursor promises an interaction that isn't there.
  const hoverClass =
    !readOnly &&
    (side === 'yes'
      ? 'hover:text-positive hover:border-positive/60'
      : 'hover:text-negative hover:border-negative/60');
  // #651 — the inactive half of the pair. Neutral by default (unchanged);
  // under sideTint it keeps its side's hue at a fraction of the active
  // strength, so the pair never reads as "one live button and one dead one".
  const restingClass = sideTint
    ? side === 'yes'
      ? 'border-positive/40 text-muted-foreground bg-positive/[0.06]'
      : 'border-negative/40 text-muted-foreground bg-negative/[0.06]'
    : 'border-border/60 text-muted-foreground';
  const sideClass = isActive
    ? side === 'yes'
      ? 'border-positive text-foreground bg-positive/10'
      : 'border-negative text-foreground bg-negative/10'
    : cn(restingClass, hoverClass);

  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const hideUnknownFeePrices = useDisplaySettings((s) => s.hideUnknownFeePrices);
  const feeEntry = useFeeFor(ticker);
  const resolvedFeeStatus = useFeeDisplayStatus(ticker, feeEntry);
  const feeStatus = feeAdjusted ? resolvedFeeStatus : 'not-applicable';
  const hiddenForUnknownFee = hideUnknownFeePrices && feeDisplayCanHide(feeStatus);
  // Taker mode pills show the ask (crossing price) so the fee burden
  // is the taker rate; Maker mode pills show the bid (resting price)
  // so the fee burden is the maker rate. Without this branch the pill
  // odds under-quote the fee in Taker mode.
  // 'market' mode displays the last trade price (a crossing event), so its
  // fee perspective is the taker rate — same as 'taker'. The maker/taker
  // narrowing came in from main alongside dan's 'market'-mode restore.
  const feePerspective = priceMode === 'maker' ? 'maker' : 'taker';
  const effectiveCents =
    cents == null
      ? null
      : effectivePriceForOdds(cents, feeAdjusted, feeEntry, feePerspective, makerRebatePct);
  const displayCents = hiddenForUnknownFee || cents == null
    ? null
    : roundToCentiCents(cents);
  const oddsBasisCents = hiddenForUnknownFee || effectiveCents == null
    ? null
    : roundToCentiCents(effectiveCents);
  const odds = oddsBasisCents != null ? americanOdds(oddsBasisCents) : null;
  const feeMessage = feeStatus === 'loading' ? null : FEE_DISPLAY_MESSAGE[feeStatus];
  // A read-only pill drops the click promise but NOT the explanation for a
  // missing price: `feeMessage` alone would contradict the pill, since it says
  // "showing the raw venue price" while the slot renders an em-dash. Why a
  // price is suppressed is a display fact, not a trading detail.
  const tradeLine =
    cents == null
      ? null
      : hiddenForUnknownFee
        ? `${readOnly ? 'Price' : 'Trade price'} hidden because its fee schedule is unavailable`
        : readOnly
          ? feeAdjusted && oddsBasisCents != null && oddsBasisCents !== displayCents
            ? `${displayCents}¢ raw; Fees odds ${odds ?? '—'} use ${oddsBasisCents}¢ ${feePerspective} basis`
            : null
          : feeAdjusted && oddsBasisCents != null && oddsBasisCents !== displayCents
            ? `Click to trade ${label} @ ${displayCents}¢ raw; Fees odds ${odds ?? '—'} use ${oddsBasisCents}¢ ${feePerspective} basis`
            : `Click to trade ${label} @ ${displayCents}¢${odds ? ` → ${odds}` : ''}`;

  // Tooltip lines in priority order. A list rather than one template because
  // #167 adds a line that must ALSO appear when the pill has no price of its
  // own to quote — a crossed book whose market-mode price is "—" is still a
  // crossed book. The value-signal line stays gated on a present price so its
  // existing behaviour is unchanged.
  const titleLines = [
    tradeLine,
    cents != null && !hiddenForUnknownFee && valueSignal
      ? 'VALUE: ask ≥2¢ below Unabated fair AND the other venue’s ask'
      : null,
    feeMessage,
    crossedBook ? `⚠ ${crossedBookTooltip(crossedBook)}` : null,
    ticker || null,
  ].filter(Boolean);

  // Always render every slot — even when cents/odds are null — and reserve
  // consistent widths so a pill missing data lines up vertically with sibling
  // pills above and below it. `min-w-[Nch]` reserves N character widths via
  // the monospace font; `tabular-nums` keeps digit columns identical. Without
  // this, a `—` pill collapses narrower than its `52¢ | -135` neighbour and
  // the Y/N pair shifts off the column's vertical centre.
  //
  // The price slot reserves `min-w-[3ch]` — two digits plus the ¢ — so a
  // single-digit price (a market that's drifted to ≤9¢) right-aligns into
  // the 2-digit footprint instead of shrinking the pill and reflowing the
  // whole row. Widening back to 2 digits (or 100¢, off the pill entirely at
  // FINAL) only ever grows past the floor, never below it.
  //
  // A pill with no price to quote reads as unavailable in both modes — no
  // price at all, or one suppressed because its fee schedule is unknown. The
  // button gets there through `disabled:opacity-40`; a span has no disabled
  // state, so it carries the same dimming outright.
  const inert = disabled || cents == null || hiddenForUnknownFee;
  const shellClass = cn(
    'relative inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[13px] leading-none',
    readOnly
      ? inert && 'opacity-40'
      : 'transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
    sideClass,
  );
  const title = titleLines.length ? titleLines.join('\n') : undefined;

  const content = (
    <>
      <span className="font-semibold">{label}</span>
      {/* Price is the headline of the pill — render it a notch larger and
          heavier than the label/odds so the number reads first. Pulses gold
          while the cross-venue value signal holds (valueSignal). */}
      <span
        className={cn(
          'min-w-[3ch] text-right text-[16px] font-bold tabular-nums',
          valueSignal && 'animate-value-gold-text-pulse',
        )}
      >
        {displayCents != null ? `${displayCents}¢` : '—'}
      </span>
      <span className="h-3 w-px self-center bg-current opacity-30" />
      <span className="min-w-[5ch] text-right tabular-nums opacity-70">
        {odds ?? '—'}
      </span>
      {/* #167 crossed-book tripwire. ABSOLUTELY positioned on purpose: these
          tables use auto layout, so any element that changes a cell's content
          width on a live tick reflows every column (the page-jitter bug the
          fixed-ch spans elsewhere in GameTable/SpreadsTotalsTable exist to
          prevent). Out of flow, it costs zero width and can never reflow.
          Amber matches the repo's existing warning treatment, and it is a
          channel nothing else in this pill owns — the border still carries
          side (green YES / red NO: always on the armed pill, and on the
          resting pill too wherever `sideTint` is on, #651) and the price font
          still carries the gold value-signal pulse. aria-hidden because the pill's title already
          states the condition in words; the glyph is a duplicate cue, not the
          only one. */}
      {crossedBook && (
        <span
          data-testid="crossed-book-marker"
          aria-hidden="true"
          className="pointer-events-none absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-amber-600 bg-amber-500 text-[10px] font-bold leading-none text-black"
        >
          !
        </span>
      )}
    </>
  );

  // The fee marker is a SIBLING of the pill, not a child of it (#602). It used
  // to be an inert `pointer-events-none` span inside the pill; now that it is a
  // popover trigger it would be a button nested inside a button — invalid DOM,
  // and every request for help would also be a click on the trade target. The
  // wrapper is a zero-padding `inline-flex`, so it shrink-wraps the pill and
  // the marker's `-left-1 -top-1` corner lands exactly where it always did.
  const marker = <FeeScheduleMarker status={feeStatus} className="absolute -left-1 -top-1" />;

  if (readOnly) {
    return (
      <span className="relative inline-flex">
        <span data-testid={testId} className={shellClass} title={title}>
          {content}
        </span>
        {marker}
      </span>
    );
  }

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        data-testid={testId}
        // NOT disabled by a crossed book: the tripwire is display-only, and a
        // suspect book stays tradable at the trader's discretion.
        disabled={inert}
        onClick={onClick}
        className={shellClass}
        title={title}
      >
        {content}
      </button>
      {marker}
    </span>
  );
}
